import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Download, Plus, Loader2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export function Backups() {
  const { t, lang, online, staff } = useApp();
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const owner = staff?.role === "owner";
  const load = () => api.get("/backups").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { if (online) load(); }, [online]);
  const createNow = async () => {
    setBusy(true);
    try { await api.post("/backups"); toast.success(t("backup_created")); await load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const download = async (b) => {
    try {
      const { data } = await api.get(`/backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = `orbit-backup-${b.created_at.slice(0, 10)}.json`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const last = list?.[0];
  const ok = last && Date.now() - new Date(last.created_at).getTime() < 36 * 3600 * 1000;
  return (
    <section className="rounded-2xl bg-white border border-border p-4 space-y-3" data-testid="auto-backups">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-heading text-xl font-bold flex items-center gap-2"><ShieldCheck className={`w-5 h-5 ${ok ? "text-[#1F9D55]" : "text-[#B9651A]"}`} />{t("auto_backups")}</h2>
        {owner && <button type="button" onClick={createNow} disabled={!online || busy} className="h-9 px-3 rounded-lg bg-[#1F4E8C] text-white text-xs font-bold flex items-center gap-1 disabled:opacity-40" data-testid="backup-now">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}{t("backup_now")}</button>}
      </div>
      <p className="text-xs text-muted-foreground">{t("auto_backups_hint")}</p>
      {!online && <p className="text-xs text-[#B9651A]">{t("offline")}</p>}
      {list && !list.length && <p className="text-sm text-muted-foreground" data-testid="backups-empty">{t("no_backups")}</p>}
      <div className="space-y-1">
        {(list || []).map((b) => (
          <div key={b.id} className="flex items-center gap-2 rounded-lg bg-secondary px-2 py-1.5 text-xs" data-testid="backup-row">
            <div className="flex-1 min-w-0"><b dir="ltr">{fmtDate(b.created_at, lang)}</b> · {t(b.source === "auto" ? "backup_auto" : "backup_manual")} · {kb(b.size)}<div className="text-muted-foreground">{t("sales")}: {b.counts?.sales ?? 0} · {t("products")}: {b.counts?.products ?? 0} · {t("nav_customers")}: {b.counts?.customers ?? 0}</div></div>
            <button type="button" onClick={() => download(b)} className="h-8 px-2 rounded bg-white border border-border font-bold flex items-center gap-1" data-testid="backup-download"><Download className="w-3.5 h-3.5" />JSON</button>
          </div>
        ))}
      </div>
    </section>
  );
}
